import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaSearch, FaDownload, FaPlus, FaPencilAlt, FaBan, FaCheck, FaChevronLeft, FaChevronRight,
  FaExclamationTriangle, FaHandshake, FaTimes, FaFilePdf, FaPaperclip, FaTrashAlt,
} from "react-icons/fa";
import Modal from "../Modal";

const API = "/api";

// Los tipos de reunión se administran en Administración > Catálogos > Tipos de Reunión.
// Colores de la etiqueta de cada tipo (se reparten según el id del tipo).
const ESTILOS_TIPO = [
  "bg-indigo-50 text-indigo-700",
  "bg-sky-50 text-sky-700",
  "bg-emerald-50 text-emerald-700",
  "bg-amber-50 text-amber-700",
  "bg-rose-50 text-rose-700",
  "bg-violet-50 text-violet-700",
  "bg-teal-50 text-teal-700",
  "bg-slate-100 text-slate-700",
];
const estiloTipo = (idTipo) => ESTILOS_TIPO[Number(idTipo) % ESTILOS_TIPO.length];

const VOTOS = [
  { value: "favor", label: "A favor", estilo: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "contra", label: "En contra", estilo: "bg-rose-50 text-rose-700 border-rose-200" },
  { value: "abstencion", label: "Abstención", estilo: "bg-slate-100 text-slate-600 border-slate-200" },
];

// Según lo que el tipo registra en Miembros ("accion_miembro"): etiqueta de la persona y fecha que se actualiza.
const ETIQUETA_SUJETO = {
  bautismo: "Persona bautizada",
  ingreso: "Persona aceptada como miembro",
};
const CAMPO_FECHA_MIEMBRO = {
  bautismo: "fecha de bautismo",
  ingreso: "fecha de ingreso",
};

const MAX_ARCHIVO_MB = 10;
const EXTENSIONES_ARCHIVO = ".pdf,.jpg,.jpeg,.png,.webp";

const nombreVoto = (valor) => VOTOS.find((v) => v.value === valor)?.label || valor;

// La API manda la fecha como YYYY-MM-DD (sin zona horaria): se formatea sin pasar por Date.
const formatoFecha = (fecha) => {
  if (!fecha) return "";
  const [anio, mes, dia] = fecha.split("-");
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${dia} ${meses[Number(mes) - 1]} ${anio}`;
};

const formatoTamano = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const FORM_VACIO = { id_tipo: "", fecha: "", asunto: "", resultado: "", dirigieron: [], votaron: [], sujetos: [] };
const LIMITE = 10;
const clasesInput =
  "w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none";

// Selector de varias personas: búsqueda por nombre y etiquetas con la selección.
// Con conVoto, cada persona lleva además su voto (a favor, en contra o abstención).
function SelectorPersonas({ etiqueta, ayuda, requerido, conVoto = false, miembros, seleccion, onChange }) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef(null);

  useEffect(() => {
    const cerrarAlClicAfuera = (e) => {
      if (contenedor.current && !contenedor.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener("mousedown", cerrarAlClicAfuera);
    return () => document.removeEventListener("mousedown", cerrarAlClicAfuera);
  }, []);

  const elegidos = useMemo(() => new Set(seleccion.map((p) => p.id_miembro)), [seleccion]);

  const sugerencias = useMemo(() => {
    const busqueda = texto.trim().toLowerCase();
    return miembros
      .filter((m) => !elegidos.has(m.id_miembro) && (!busqueda || m.nombre.toLowerCase().includes(busqueda)))
      .slice(0, 8);
  }, [miembros, elegidos, texto]);

  const agregar = (m) => {
    onChange([...seleccion, { id_miembro: m.id_miembro, nombre: m.nombre, ...(conVoto ? { voto: "favor" } : {}) }]);
    setTexto("");
    setAbierto(false);
  };

  const quitar = (id) => onChange(seleccion.filter((p) => p.id_miembro !== id));
  const cambiarVoto = (id, voto) => onChange(seleccion.map((p) => (p.id_miembro === id ? { ...p, voto } : p)));

  return (
    <div ref={contenedor}>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {etiqueta} {requerido && <span className="text-rose-500">*</span>}
        <span className="ml-2 font-normal text-slate-400">({seleccion.length})</span>
      </label>

      {seleccion.length > 0 && !conVoto && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {seleccion.map((p) => (
            <span key={p.id_miembro} className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium pl-2.5 pr-1.5 py-1 rounded-full">
              {p.nombre}
              <button
                type="button"
                onClick={() => quitar(p.id_miembro)}
                aria-label={`Quitar a ${p.nombre}`}
                className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-indigo-100"
              >
                <FaTimes className="text-[9px]" />
              </button>
            </span>
          ))}
        </div>
      )}

      {seleccion.length > 0 && conVoto && (
        <ul className="mb-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
          {seleccion.map((p) => {
            const estilo = VOTOS.find((v) => v.value === p.voto)?.estilo || "";
            return (
              <li key={p.id_miembro} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <span className="flex-1 truncate text-slate-700">{p.nombre}</span>
                <select
                  value={p.voto}
                  onChange={(e) => cambiarVoto(p.id_miembro, e.target.value)}
                  aria-label={`Voto de ${p.nombre}`}
                  className={`text-xs font-medium border rounded-md px-2 py-1 outline-none ${estilo}`}
                >
                  {VOTOS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => quitar(p.id_miembro)}
                  aria-label={`Quitar a ${p.nombre}`}
                  className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <FaTimes className="text-[10px]" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="relative">
        <input
          type="text"
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)}
          onClick={() => setAbierto(true)}
          placeholder="Buscar persona para agregar..."
          className={clasesInput}
        />
        {abierto && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm">
            {sugerencias.length === 0 ? (
              <li className="px-3 py-2 text-slate-400">Sin coincidencias</li>
            ) : (
              sugerencias.map((m) => (
                <li key={m.id_miembro}>
                  <button
                    type="button"
                    onClick={() => agregar(m)}
                    className="w-full text-left px-3 py-2 text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
                  >
                    {m.nombre}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      {ayuda && <p className="text-xs text-slate-400 mt-1">{ayuda}</p>}
    </div>
  );
}

// Lista corta en la tabla: los primeros nombres y "+N" con el resto en el tooltip.
function ListaPersonas({ personas }) {
  if (!personas.length) return <span className="text-slate-400">-</span>;
  const visibles = personas.slice(0, 2).map((p) => p.nombre).join(", ");
  const resto = personas.length - 2;
  return (
    <span title={personas.map((p) => p.nombre).join(", ")}>
      {visibles}
      {resto > 0 && <span className="text-slate-400"> +{resto}</span>}
    </span>
  );
}

// Resumen de la votación en la tabla.
function ResumenVotos({ reunion }) {
  if (!reunion.votaron.length) return <span className="text-slate-400">-</span>;
  const detalle = reunion.votaron.map((v) => `${v.nombre}: ${nombreVoto(v.voto)}`).join("\n");
  return (
    <span className="flex flex-wrap gap-1" title={detalle}>
      {VOTOS.map((v) =>
        reunion.votos[v.value] > 0 ? (
          <span key={v.value} className={`px-2 py-0.5 text-xs rounded-full border font-medium ${v.estilo}`}>
            {reunion.votos[v.value]} {v.label.toLowerCase()}
          </span>
        ) : null
      )}
    </span>
  );
}

export default function ReunionesAdmin() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [descargandoActa, setDescargandoActa] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("todos");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [tipos, setTipos] = useState([]);

  const [miembros, setMiembros] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [archivosActuales, setArchivosActuales] = useState([]);
  const [archivosNuevos, setArchivosNuevos] = useState([]);
  const [errorForm, setErrorForm] = useState("");
  const [guardando, setGuardando] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [accionTipo, setAccionTipo] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [archivoAQuitar, setArchivoAQuitar] = useState(null);

  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3500);
  };

  const totalPages = Math.ceil(total / LIMITE);
  const tipoElegido = tipos.find((t) => String(t.id_tipo) === String(form.id_tipo));
  const campoSujeto = tipoElegido ? ETIQUETA_SUJETO[tipoElegido.accion_miembro] : null;
  // En el formulario solo se ofrecen tipos activos (más el que ya tenga la reunión que se edita).
  const tiposParaElegir = tipos.filter((t) => t.activo || String(t.id_tipo) === String(form.id_tipo));

  // La búsqueda espera a que se deje de teclear para no consultar en cada letra.
  useEffect(() => {
    const t = setTimeout(() => { setBusqueda(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const construirQuery = useCallback(
    (pagina, limite) =>
      `page=${pagina}&limit=${limite}&search=${encodeURIComponent(busqueda)}&estatus=${estatusFiltro}&id_tipo=${tipoFiltro}`,
    [busqueda, estatusFiltro, tipoFiltro]
  );

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/reuniones?${construirQuery(page, LIMITE)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo reuniones");
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      console.error(error);
      mostrarToast("Error cargando las reuniones", "error");
    } finally {
      setLoading(false);
    }
  }, [page, construirQuery]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/reuniones/tipos`, { credentials: "include" });
        if (!res.ok) throw new Error("Error obteniendo tipos");
        setTipos(await res.json());
      } catch (error) {
        console.error(error);
        mostrarToast("Error cargando los tipos de reunión", "error");
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/miembros?estatus=activos`, { credentials: "include" });
        if (!res.ok) throw new Error("Error obteniendo miembros");
        const lista = await res.json();
        setMiembros(
          lista.map((m) => ({
            id_miembro: m.id_miembro,
            nombre: [m.nombre, m.paterno, m.materno].filter(Boolean).join(" "),
          }))
        );
      } catch (error) {
        console.error(error);
        mostrarToast("Error cargando la lista de miembros", "error");
      }
    })();
  }, []);

  const descargarBlob = (blob, nombre) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // =========================
  // EXPORTAR CSV (todas las reuniones del filtro actual)
  // =========================
  const exportarCSV = async () => {
    setExportando(true);
    try {
      const filas = [];
      for (let pagina = 1; ; pagina++) {
        const res = await fetch(`${API}/reuniones?${construirQuery(pagina, 100)}`, { credentials: "include" });
        if (!res.ok) throw new Error("Error exportando");
        const result = await res.json();
        filas.push(...(result.data || []));
        if (filas.length >= (result.total || 0) || !(result.data || []).length) break;
      }

      const celda = (valor) => {
        let texto = String(valor ?? "");
        // Evita que Excel interprete el texto como fórmula.
        if (/^[=+\-@\t\r]/.test(texto)) texto = `'${texto}`;
        return `"${texto.replace(/"/g, '""')}"`;
      };
      const encabezados = ["Fecha", "Tipo", "Asunto", "Llevaron la reunión", "Persona(s) bautizada(s) o aceptada(s)", "Votaron", "A favor", "En contra", "Abstenciones", "Resultado", "Adjuntos", "Estatus"];
      const lineas = [encabezados.map(celda).join(",")];
      filas.forEach((r) => {
        lineas.push([
          formatoFecha(r.fecha),
          r.tipo,
          r.asunto,
          r.dirigieron.map((p) => p.nombre).join("; "),
          r.sujetos.map((p) => p.nombre).join("; "),
          r.votaron.map((p) => `${p.nombre} (${nombreVoto(p.voto)})`).join("; "),
          r.votos.favor,
          r.votos.contra,
          r.votos.abstencion,
          r.resultado,
          r.archivos.length,
          r.activo ? "Activo" : "Inactivo",
        ].map(celda).join(","));
      });

      descargarBlob(new Blob(["﻿" + lineas.join("\n")], { type: "text/csv;charset=utf-8;" }), "reuniones.csv");
    } catch (error) {
      console.error(error);
      mostrarToast("No se pudo exportar", "error");
    } finally {
      setExportando(false);
    }
  };

  // =========================
  // ACTA EN PDF
  // =========================
  const descargarActa = async (r) => {
    setDescargandoActa(r.id_reunion);
    try {
      const res = await fetch(`${API}/reuniones/${r.id_reunion}/acta`, { credentials: "include" });
      if (!res.ok) throw new Error("Error generando el acta");
      descargarBlob(await res.blob(), `acta_${r.id_reunion}_${r.fecha}.pdf`);
    } catch (error) {
      console.error(error);
      mostrarToast("No se pudo generar el acta", "error");
    } finally {
      setDescargandoActa(null);
    }
  };

  // =========================
  // CRUD
  // =========================
  const abrirNuevo = () => {
    setEditandoId(null);
    setForm(FORM_VACIO);
    setArchivosActuales([]);
    setArchivosNuevos([]);
    setErrorForm("");
    setModalOpen(true);
  };

  const abrirEditar = (r) => {
    setEditandoId(r.id_reunion);
    setForm({
      id_tipo: r.id_tipo, fecha: r.fecha, asunto: r.asunto, resultado: r.resultado,
      dirigieron: r.dirigieron, votaron: r.votaron, sujetos: r.sujetos,
    });
    setArchivosActuales(r.archivos);
    setArchivosNuevos([]);
    setErrorForm("");
    setModalOpen(true);
  };

  const elegirArchivos = (e) => {
    const elegidos = Array.from(e.target.files || []);
    e.target.value = "";
    const demasiado = elegidos.find((f) => f.size > MAX_ARCHIVO_MB * 1024 * 1024);
    if (demasiado) return setErrorForm(`"${demasiado.name}" pesa más de ${MAX_ARCHIVO_MB} MB`);
    if (archivosActuales.length + archivosNuevos.length + elegidos.length > 20) return setErrorForm("Cada reunión admite hasta 20 archivos");
    setErrorForm("");
    setArchivosNuevos((previos) => [...previos, ...elegidos]);
  };

  const subirArchivos = async (idReunion) => {
    const cuerpo = new FormData();
    archivosNuevos.forEach((f) => cuerpo.append("archivos", f));
    const res = await fetch(`${API}/reuniones/${idReunion}/archivos`, { method: "POST", credentials: "include", body: cuerpo });
    if (!res.ok) {
      const cuerpoError = await res.json().catch(() => ({}));
      throw new Error(cuerpoError.mensaje || "Error subiendo los archivos");
    }
  };

  const guardar = async () => {
    if (!form.id_tipo) return setErrorForm("Elige el tipo de reunión");
    if (!form.fecha) return setErrorForm("Indica la fecha");
    if (form.dirigieron.length === 0) return setErrorForm("Indica quién llevó la reunión");
    if (!form.asunto.trim()) return setErrorForm("Escribe el asunto que se trató");
    if (!form.resultado.trim()) return setErrorForm("Escribe el resultado");

    setGuardando(true);
    setErrorForm("");
    try {
      const res = await fetch(
        editandoId ? `${API}/reuniones/${editandoId}` : `${API}/reuniones`,
        {
          method: editandoId ? "PUT" : "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_tipo: Number(form.id_tipo),
            fecha: form.fecha,
            asunto: form.asunto,
            resultado: form.resultado,
            dirigieron: form.dirigieron.map((p) => p.id_miembro),
            votaron: form.votaron.map((p) => ({ id_miembro: p.id_miembro, voto: p.voto })),
            sujetos: campoSujeto ? form.sujetos.map((p) => p.id_miembro) : [],
          }),
        }
      );
      if (!res.ok) {
        const cuerpo = await res.json().catch(() => ({}));
        return setErrorForm(cuerpo.mensaje || "Error guardando la reunión");
      }
      const guardada = await res.json();

      // Los archivos se suben después de guardar: la reunión ya existe aunque falle la carga.
      if (archivosNuevos.length > 0) {
        try {
          await subirArchivos(guardada.id_reunion);
        } catch (error) {
          setModalOpen(false);
          mostrarToast(`La reunión se guardó, pero no se pudieron adjuntar los archivos: ${error.message}. Ábrela para reintentar.`, "error");
          cargar();
          return;
        }
      }

      setModalOpen(false);
      mostrarToast(editandoId ? "Reunión actualizada" : "Reunión registrada");
      cargar();
    } catch (error) {
      console.error(error);
      setErrorForm("Error inesperado al guardar");
    } finally {
      setGuardando(false);
    }
  };

  const quitarArchivo = async () => {
    try {
      const res = await fetch(`${API}/reuniones/archivos/${archivoAQuitar.id_archivo}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Error quitando el archivo");
      setArchivosActuales((lista) => lista.filter((a) => a.id_archivo !== archivoAQuitar.id_archivo));
      setArchivoAQuitar(null);
      cargar();
    } catch (error) {
      console.error(error);
      setArchivoAQuitar(null);
      setErrorForm("No se pudo quitar el archivo");
    }
  };

  const abrirConfirm = (id, tipo) => {
    setSelectedId(id);
    setAccionTipo(tipo);
    setConfirmOpen(true);
  };

  const ejecutarAccion = async () => {
    try {
      const desactivar = accionTipo === "desactivar";
      const res = await fetch(
        desactivar ? `${API}/reuniones/${selectedId}` : `${API}/reuniones/${selectedId}/reactivar`,
        { method: desactivar ? "DELETE" : "PATCH", credentials: "include" }
      );
      if (!res.ok) throw new Error("Error cambiando estatus");
      setConfirmOpen(false);
      setSelectedId(null);
      mostrarToast(desactivar ? "Reunión desactivada" : "Reunión reactivada");
      cargar();
    } catch (error) {
      console.error(error);
      mostrarToast("No se pudo cambiar el estatus", "error");
    }
  };

  const inicioRango = total === 0 ? 0 : (page - 1) * LIMITE + 1;
  const finRango = Math.min(page * LIMITE, total);
  const botonAccion = "w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 transition";

  return (
    <div className="p-6">

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] max-w-sm px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600">Administración</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-600 font-medium">Reuniones</span>
      </div>

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reuniones</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Reuniones, bautismos, aceptación de miembros, reuniones extraordinarias y de negocio.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportarCSV}
            disabled={exportando}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
          >
            <FaDownload className="text-xs" /> {exportando ? "Exportando..." : "Exportar"}
          </button>

          <button
            onClick={abrirNuevo}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            <FaPlus className="text-xs" /> Nuevo registro
          </button>
        </div>
      </div>

      {/* BARRA DE FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por asunto, resultado o persona..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Tipo</label>
          <select
            value={tipoFiltro}
            onChange={(e) => { setTipoFiltro(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="">Todos</option>
            {tipos.map((t) => <option key={t.id_tipo} value={t.id_tipo}>{t.nombre}{t.activo ? "" : " (inactivo)"}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => { setEstatusFiltro(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                <th className="px-4 py-3 text-left font-semibold">Asunto</th>
                <th className="px-4 py-3 text-left font-semibold">Llevó la reunión</th>
                <th className="px-4 py-3 text-left font-semibold">Votación</th>
                <th className="px-4 py-3 text-left font-semibold">Resultado</th>
                <th className="px-4 py-3 text-left font-semibold">Estatus</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">Cargando...</td></tr>
              ) : data.length > 0 ? (
                data.map((r) => (
                  <tr key={r.id_reunion} className="border-t border-slate-100 hover:bg-slate-50 transition align-top">
                    <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">
                      <span className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                          <FaHandshake className="text-xs" />
                        </span>
                        {formatoFecha(r.fecha)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${estiloTipo(r.id_tipo)}`}>
                        {r.tipo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[260px]">
                      <span className="line-clamp-2" title={r.asunto}>{r.asunto}</span>
                      {r.sujetos.length > 0 && (
                        <span className="block text-xs text-slate-500 mt-0.5" title={r.sujetos.map((p) => p.nombre).join(", ")}>
                          {ETIQUETA_SUJETO[r.accion_miembro] || "Persona"}: {r.sujetos.map((p) => p.nombre).join(", ")}
                        </span>
                      )}
                      {r.archivos.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400 mt-0.5" title={r.archivos.map((a) => a.nombre).join("\n")}>
                          <FaPaperclip className="text-[10px]" /> {r.archivos.length} {r.archivos.length === 1 ? "archivo" : "archivos"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700"><ListaPersonas personas={r.dirigieron} /></td>
                    <td className="px-4 py-3 text-slate-700"><ResumenVotos reunion={r} /></td>
                    <td className="px-4 py-3 text-slate-700 max-w-[260px]">
                      <span className="line-clamp-2" title={r.resultado}>{r.resultado}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className={`w-2 h-2 rounded-full ${r.activo ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {r.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => descargarActa(r)}
                          disabled={descargandoActa === r.id_reunion}
                          title="Descargar acta en PDF"
                          className={`${botonAccion} hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 disabled:opacity-50`}
                        >
                          <FaFilePdf className="text-xs" />
                        </button>

                        <button
                          onClick={() => abrirEditar(r)}
                          title="Editar"
                          className={`${botonAccion} hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200`}
                        >
                          <FaPencilAlt className="text-xs" />
                        </button>

                        {r.activo ? (
                          <button
                            onClick={() => abrirConfirm(r.id_reunion, "desactivar")}
                            title="Desactivar"
                            className={`${botonAccion} hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200`}
                          >
                            <FaBan className="text-xs" />
                          </button>
                        ) : (
                          <button
                            onClick={() => abrirConfirm(r.id_reunion, "reactivar")}
                            title="Reactivar"
                            className={`${botonAccion} hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200`}
                          >
                            <FaCheck className="text-xs" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINACIÓN */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-slate-500">
          {total === 0 ? "Sin resultados" : `Mostrando ${inicioRango} a ${finRango} de ${total} resultados`}
        </span>

        <div className="flex items-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronLeft className="text-xs" />
          </button>

          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-indigo-600 text-white text-sm font-semibold">
            {page}
          </span>

          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>

      {/* MODAL NUEVO / EDITAR */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editandoId ? "Editar registro" : "Nuevo registro"}
        maxWidth="max-w-2xl"
      >
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Tipo <span className="text-rose-500">*</span>
              </label>
              <select
                value={form.id_tipo}
                onChange={(e) => setForm({ ...form, id_tipo: e.target.value })}
                className={clasesInput}
              >
                <option value="">-- Selecciona --</option>
                {tiposParaElegir.map((t) => <option key={t.id_tipo} value={t.id_tipo}>{t.nombre}{t.activo ? "" : " (inactivo)"}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Fecha <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                className={clasesInput}
              />
            </div>
          </div>

          <SelectorPersonas
            etiqueta="Quién llevó la reunión"
            requerido
            miembros={miembros}
            seleccion={form.dirigieron}
            onChange={(dirigieron) => setForm({ ...form, dirigieron })}
          />

          {campoSujeto && (
            <SelectorPersonas
              etiqueta={campoSujeto}
              ayuda={`Si aún no está en Miembros, regístrala primero. Su ${CAMPO_FECHA_MIEMBRO[tipoElegido.accion_miembro]} se actualiza sola en Miembros con la fecha de este registro.`}
              miembros={miembros}
              seleccion={form.sujetos}
              onChange={(sujetos) => setForm({ ...form, sujetos })}
            />
          )}

          <SelectorPersonas
            etiqueta="Quiénes votaron"
            conVoto
            miembros={miembros}
            seleccion={form.votaron}
            onChange={(votaron) => setForm({ ...form, votaron })}
          />

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Asunto tratado <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              maxLength={5000}
              value={form.asunto}
              onChange={(e) => setForm({ ...form, asunto: e.target.value })}
              className={clasesInput}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Resultado <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              maxLength={5000}
              value={form.resultado}
              onChange={(e) => setForm({ ...form, resultado: e.target.value })}
              className={clasesInput}
            />
          </div>

          {/* ARCHIVOS ADJUNTOS */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Archivos adjuntos
              <span className="ml-2 font-normal text-slate-400">PDF, JPG, PNG o WEBP · hasta {MAX_ARCHIVO_MB} MB cada uno</span>
            </label>

            {(archivosActuales.length > 0 || archivosNuevos.length > 0) && (
              <ul className="mb-2 border border-slate-200 rounded-lg divide-y divide-slate-100">
                {archivosActuales.map((a) => (
                  <li key={a.id_archivo} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                    <FaPaperclip className="text-slate-400 text-xs shrink-0" />
                    <a
                      href={`${API}/reuniones/archivos/${a.id_archivo}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 truncate text-indigo-600 hover:underline"
                    >
                      {a.nombre}
                    </a>
                    <span className="text-xs text-slate-400">{formatoTamano(a.tamano_bytes)}</span>
                    <button
                      type="button"
                      onClick={() => setArchivoAQuitar(a)}
                      aria-label={`Quitar ${a.nombre}`}
                      className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                    >
                      <FaTrashAlt className="text-[10px]" />
                    </button>
                  </li>
                ))}
                {archivosNuevos.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-50/40">
                    <FaPaperclip className="text-indigo-400 text-xs shrink-0" />
                    <span className="flex-1 truncate text-slate-700">{f.name}</span>
                    <span className="text-xs text-slate-400">{formatoTamano(f.size)} · se sube al guardar</span>
                    <button
                      type="button"
                      onClick={() => setArchivosNuevos((lista) => lista.filter((_, j) => j !== i))}
                      aria-label={`Quitar ${f.name}`}
                      className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                      <FaTimes className="text-[10px]" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-dashed border-slate-300 text-slate-600 hover:bg-slate-50 cursor-pointer transition">
              <FaPlus className="text-xs" /> Agregar archivos
              <input type="file" multiple accept={EXTENSIONES_ARCHIVO} onChange={elegirArchivos} className="sr-only" />
            </label>
          </div>

          {errorForm && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{errorForm}</p>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              onClick={() => setModalOpen(false)}
              className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={guardar}
              disabled={guardando}
              className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition disabled:opacity-50"
            >
              {guardando ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* MODAL CONFIRMAR QUITAR ARCHIVO */}
      <Modal open={!!archivoAQuitar} onClose={() => setArchivoAQuitar(null)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-rose-50 text-rose-500">
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Quitar archivo</h3>
          <p className="text-sm text-slate-500 mb-6 break-words">¿Quitar "{archivoAQuitar?.nombre}" de esta reunión?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setArchivoAQuitar(null)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={quitarArchivo}
              className="w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition bg-rose-600 hover:bg-rose-700"
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>

      {/* MODAL CONFIRMAR DESACTIVAR / REACTIVAR */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
            accionTipo === "desactivar" ? "bg-rose-50 text-rose-500" : "bg-emerald-50 text-emerald-500"
          }`}>
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            {accionTipo === "desactivar" ? "Desactivar" : "Reactivar"} registro
          </h3>
          <p className="text-sm text-slate-500 mb-6">¿Deseas continuar?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmOpen(false)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={ejecutarAccion}
              className={`w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition ${
                accionTipo === "desactivar" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
